import { getWeeksInMonth } from "date-fns";
import { isAvailableAsync } from "expo-calendar";
import { useContext, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { CalendarContext } from "react-native-calendars";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { Button } from "~/components/Button";
import {
    DropdownContent,
    DropdownItem,
    DropdownItemIcon,
    DropdownItemTitle,
    DropdownRoot,
    DropdownTrigger,
} from "~/components/dropdown";
import { SyncCalendar } from "~/components/form/calendar/SyncCalendar";
import { SearchInput } from "~/components/form/SearchInput";
import { ShareContent } from "~/components/form/team/ShareContent";
import { CalendarGroup } from "~/components/layout/calendar/CalendarGroup";
import {
    DATE_CALENDAR_HEADER_HEIGHT,
    DATE_CALENDAR_LINE_HEIGHT,
    DATE_SELECTOR_HEIGHT,
    MonthlyCalendar,
} from "~/components/layout/calendar/MonthlyCalendar";
import { Page } from "~/components/layout/Page";
import { CanAny } from "~/components/permission/CanAny";
import { Only } from "~/components/permission/Only";

import { Space } from "~/constants/sizing";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { setWidthOnLayout } from "~/util/layout";

const TOOLBAR_HEIGHT = 50;
const HEADER_GAP = Space.lg;
const HEADER_PADDING = Space.md;

const HEADER_MIN_HEIGHT = HEADER_PADDING + TOOLBAR_HEIGHT + HEADER_GAP + DATE_SELECTOR_HEIGHT + HEADER_PADDING;

export default function Calendar() {
    const { background } = useTheme();
    const { date } = useContext(CalendarContext);
    const [calendarHeight, setCalendarHeight] = useState(
        DATE_CALENDAR_HEADER_HEIGHT + getWeeksInMonth(date) * DATE_CALENDAR_LINE_HEIGHT,
    );
    const { can, canAny } = useProfile();
    const { calendarForMonth } = useJournal();
    const [expanded, setExpanded] = useState(true);
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const [search, setSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [syncable, setSyncable] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        isAvailableAsync().then(setSyncable);
    }, []);

    const showCalendar = useMemo(() => expanded && !search, [expanded, search]);

    const height = useMemo(
        () => (showCalendar ? HEADER_MIN_HEIGHT + calendarHeight : HEADER_MIN_HEIGHT),
        [showCalendar, calendarHeight],
    );
    const maxHeight = useSharedValue(height);

    // biome-ignore lint/correctness/useExhaustiveDependencies(maxHeight): shared value
    useEffect(() => {
        maxHeight.value = withTiming(height);
    }, [height]);

    const calendar = useMemo(() => {
        return calendarForMonth(date, search ? searchTerm : null);
    }, [calendarForMonth, date, search, searchTerm]);

    const can_sync_to_calendar = syncable && can("view", "calendar");
    const can_share =
        can("update", "team") && canAny("view", ["activities", "calendar", "journal", "symptoms", "cycle"]);

    return (
        <Page tabs padding={Space.none} scrollable={false} gap={0}>
            <Animated.View style={[styles.header, background.primary, { maxHeight }]} onLayout={onLayout}>
                <Only when={search}>
                    <View style={[styles.toolbar, { width }]}>
                        <SearchInput
                            value={searchTerm}
                            onChangeText={setSearchTerm}
                            hideLabel
                            containerStyle={{ flexGrow: 1 }}
                        />
                        <Button onPress={() => setSearch(false)} variant="quiet" title="Cancel" />
                    </View>
                </Only>
                <Only not when={search}>
                    <View style={[styles.toolbar, { width }]}>
                        {(can_sync_to_calendar || can_share) && (
                            <DropdownRoot>
                                <DropdownTrigger style={{ background: "transparent", borderWidth: 0 }}>
                                    <Button icon="share" variant="quiet" size="lg" accessibilityRole="spinbutton" />
                                </DropdownTrigger>
                                <DropdownContent>
                                    {can_sync_to_calendar && (
                                        <DropdownItem key="sync-calendar" onSelect={() => setSyncing(true)}>
                                            <DropdownItemTitle>Sync to phone calendar</DropdownItemTitle>
                                            <DropdownItemIcon icon="share" ios={{ name: "calendar" }} />
                                        </DropdownItem>
                                    )}
                                    {can_share && (
                                        <DropdownItem key="share-logs" onSelect={() => setSharing(true)}>
                                            <DropdownItemTitle>Share logs</DropdownItemTitle>
                                            <DropdownItemIcon icon="share" ios={{ name: "tray.and.arrow.up" }} />
                                        </DropdownItem>
                                    )}
                                </DropdownContent>
                            </DropdownRoot>
                        )}
                        <DropdownRoot>
                            <DropdownTrigger style={{ background: "transparent", borderWidth: 0 }}>
                                <Button icon="calendar" variant="quiet" size="lg" accessibilityRole="spinbutton" />
                            </DropdownTrigger>
                            <DropdownContent>
                                <DropdownItem
                                    key="calendar-view"
                                    onSelect={() => setExpanded(true)}
                                    disabled={expanded}
                                >
                                    <DropdownItemTitle selected={expanded}>Calendar view</DropdownItemTitle>
                                    <DropdownItemIcon icon="calendar" ios={{ name: "calendar" }} selected={expanded} />
                                </DropdownItem>
                                <DropdownItem key="list-view" onSelect={() => setExpanded(false)} disabled={!expanded}>
                                    <DropdownItemTitle selected={!expanded}>List view</DropdownItemTitle>
                                    <DropdownItemIcon icon="list" ios={{ name: "list.bullet" }} selected={!expanded} />
                                </DropdownItem>
                            </DropdownContent>
                        </DropdownRoot>
                        <Button icon="search" variant="quiet" size="lg" onPress={() => setSearch(true)} />
                        <CanAny ability="update" section={["activities", "journal", "symptoms", "calendar"]}>
                            <Button
                                icon="plus"
                                variant="primary"
                                size="lg"
                                href={{ pathname: "/journal/add", params: { date } }}
                            />
                        </CanAny>
                    </View>
                </Only>
                <View>
                    <MonthlyCalendar
                        hideExtraDays
                        onCalendarHeightChange={setCalendarHeight}
                        toolbarProps={{ style: { width } }}
                    />
                </View>
            </Animated.View>
            <View style={styles.journals}>
                <CalendarGroup calendar={calendar} />
            </View>
            {can_sync_to_calendar && syncing && <SyncCalendar onDismiss={() => setSyncing(false)} />}
            {can_share && sharing && (
                <ShareContent
                    title="Share logs"
                    initial={["activities", "calendar", "journal", "symptoms", "photos", "cycle"]}
                    onRequestClose={() => setSharing(false)}
                />
            )}
        </Page>
    );
}

const styles = StyleSheet.create({
    header: {
        alignItems: "stretch",
        gap: HEADER_GAP,
        paddingTop: HEADER_PADDING,
        paddingBottom: HEADER_PADDING,
        overflow: "hidden",
        minHeight: HEADER_MIN_HEIGHT,
    },
    toolbar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: Space.sm,
        paddingLeft: Space.sm,
        paddingRight: Space.sm,
        height: TOOLBAR_HEIGHT,
    },
    journals: {
        flex: 1,
        paddingLeft: Space.lg,
        paddingRight: Space.lg,
    },
});
